import React, { useMemo } from "react";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const FeaturesGrid = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const features = useMemo(
    () => [
      {
        title: tt("featuresGrid.items.workflow22.title", "22-tool workflow"),
        text: tt(
          "featuresGrid.items.workflow22.text",
          "Move across hooks, titles, captions, descriptions, scripts, rewrites, and strategic creator tools from one connected workspace."
        ),
      },
      {
        title: tt("featuresGrid.items.freeCoreWriting.title", "Free core writing tools"),
        text: tt(
          "featuresGrid.items.freeCoreWriting.text",
          "Start with essential tools for hooks, titles, captions, hashtags, descriptions, and scripts."
        ),
      },
      {
        title: tt("featuresGrid.items.creatorPremium.title", "Creator premium workflow"),
        text: tt(
          "featuresGrid.items.creatorPremium.text",
          "Unlock stronger packaging, rewrites, repurposing, CTA support, thumbnail support, offer positioning, and retention help."
        ),
      },
      {
        title: tt("featuresGrid.items.proStrategic.title", "Pro strategic tools"),
        text: tt(
          "featuresGrid.items.proStrategic.text",
          "Use advanced tools for turning trends into content, training brand voice, and differentiating your content."
        ),
      },
      {
        title: tt("featuresGrid.items.hooksAngles.title", "Hooks and hook angles"),
        text: tt(
          "featuresGrid.items.hooksAngles.text",
          "Generate stronger openings and explore multiple directions from one topic."
        ),
      },
      {
        title: tt("featuresGrid.items.titlesCaptionsHashtags.title", "Titles, captions, and hashtags"),
        text: tt(
          "featuresGrid.items.titlesCaptionsHashtags.text",
          "Write stronger packaging elements that help your content feel clearer, more clickable, and easier to publish."
        ),
      },
      {
        title: tt("featuresGrid.items.descriptionsScriptsCtas.title", "Descriptions, scripts, and CTAs"),
        text: tt(
          "featuresGrid.items.descriptionsScriptsCtas.text",
          "Build stronger long-form support, short scripts, and clearer calls to action from the same core idea."
        ),
      },
      {
        title: tt("featuresGrid.items.thumbnailHookScore.title", "Thumbnail text and hook score"),
        text: tt(
          "featuresGrid.items.thumbnailHookScore.text",
          "Improve first impression and analyze hook strength before you publish."
        ),
      },
      {
        title: tt("featuresGrid.items.humanizerRewriteRepurpose.title", "Humanizer, rewrites, and repurposing"),
        text: tt(
          "featuresGrid.items.humanizerRewriteRepurpose.text",
          "Make your writing sound more natural, reshape ideas, and turn one strong input into multiple content outputs."
        ),
      },
      {
        title: tt(
          "featuresGrid.items.retentionVoiceDifferentiation.title",
          "Retention, brand voice, and differentiation"
        ),
        text: tt(
          "featuresGrid.items.retentionVoiceDifferentiation.text",
          "Sharpen how your content feels, keeps attention, and stands apart from generic creator copy."
        ),
      },
      {
        title: tt(
          "featuresGrid.items.trendCommentToContent.title",
          "Trends and comment-driven content"
        ),
        text: tt(
          "featuresGrid.items.trendCommentToContent.text",
          "Turn trends and audience signals into stronger content ideas, angles, and execution paths."
        ),
      },
      {
        title: tt(
          "featuresGrid.items.librarySavedAssets.title",
          "Library and saved assets"
        ),
        text: tt(
          "featuresGrid.items.librarySavedAssets.text",
          "Keep your strongest hooks and Studio outputs saved so your workflow gets faster over time."
        ),
      },
    ],
    [t]
  );

  return (
    <section style={{ padding: "20px 20px 36px", background: colors.sectionBg }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ marginBottom: "24px", textAlign: "center" }}>
          <h2
            style={{
              margin: "0 0 12px",
              color: colors.text,
              fontSize: "clamp(2rem, 4vw, 3rem)",
              lineHeight: "1.08",
            }}
          >
            {tt("featuresGrid.title", "What Hookora helps you create")}
          </h2>
          <p
            style={{
              maxWidth: "820px",
              margin: "0 auto",
              color: colors.textMuted,
              fontSize: "16px",
              lineHeight: "1.7",
            }}
          >
            {tt(
              "featuresGrid.subtitle",
              "A full creator workflow built across free, premium, and advanced strategic tools."
            )}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "18px",
          }}
        >
          {features.map((feature, index) => (
            <div
              key={feature.title}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderRadius: "22px",
                padding: "22px",
                boxShadow: colors.shadow,
                backdropFilter: isDark ? "blur(14px)" : "none",
              }}
            >
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "12px",
                  background:
                    index % 2 === 0
                      ? "linear-gradient(135deg, #2563eb, #7c3aed)"
                      : "linear-gradient(135deg, #16a34a, #059669)",
                  marginBottom: "16px",
                }}
              />

              <h3
                style={{
                  margin: "0 0 10px",
                  color: colors.text,
                  fontSize: "20px",
                  lineHeight: "1.3",
                }}
              >
                {feature.title}
              </h3>

              <p
                style={{
                  margin: 0,
                  color: colors.textMuted,
                  lineHeight: "1.7",
                  fontSize: "15px",
                }}
              >
                {feature.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;